import { HERALD_PDF_EXCLUDE_DATA_ATTR } from "@/lib/chat/heraldChatDomDataAttrs";

const HERALD_PDF_EXCLUDE_SELECTOR = `[${HERALD_PDF_EXCLUDE_DATA_ATTR}]`;

const CHART_SLOT_SELECTOR = '[data-slot="chart"]';
const RECHARTS_RESPONSIVE_CONTAINER_SELECTOR = ".recharts-responsive-container";
const RECHARTS_WRAPPER_SELECTOR = ".recharts-wrapper";
const RECHARTS_SURFACE_SELECTOR = "svg.recharts-surface";
const RECHARTS_LEGEND_WRAPPER_SELECTOR = ".recharts-legend-wrapper";
const CARD_CONTENT_SELECTOR = '[data-slot="card-content"]';
/** `px-4` horizontal padding on {@link CardContent} — not in PDF CSS without Tailwind. */
const PDF_CARD_CONTENT_HORIZONTAL_PADDING_PX = 32;

/** A4 portrait with 1cm margins — printable content width at 96 CSS px/in. */
export const HERALD_PDF_A4_CONTENT_MAX_WIDTH_PX = Math.round(
  ((210 - 20) / 25.4) * 96,
);

/** Visible shrink so axis ticks and card rings are not clipped in Gotenberg. */
export const HERALD_PDF_CHART_WIDTH_SCALE = 0.9;

/** Matches live layout (incl. legend band); viewBox aspect ratio is often wider than the rendered slot. */
const pdfChartHeightForWidth = (
  widthPx: number,
  baseWidthPx: number,
  baseHeightPx: number,
): number =>
  Math.max(
    140,
    Math.round(baseHeightPx * (widthPx / Math.max(baseWidthPx, 1))),
  );

/**
 * PDF HTML has no Tailwind — Recharts keeps live px on SVG/container in the clone.
 * Inline explicit dimensions on slot, container, and svg so the chart actually scales.
 */
const syncPdfRechartsDimensionsOnChartSlot = (
  chartSlot: HTMLElement,
  widthPx: number,
  baseWidthPx: number,
  baseHeightPx: number,
): void => {
  const heightPx = pdfChartHeightForWidth(widthPx, baseWidthPx, baseHeightPx);

  chartSlot.style.boxSizing = "border-box";
  chartSlot.style.display = "block";
  chartSlot.style.width = `${widthPx}px`;
  chartSlot.style.maxWidth = "100%";
  chartSlot.style.height = `${heightPx}px`;
  chartSlot.style.minWidth = "0";
  chartSlot.style.minHeight = `${heightPx}px`;
  chartSlot.style.marginLeft = "auto";
  chartSlot.style.marginRight = "auto";
  chartSlot.style.overflow = "hidden";

  for (const rc of chartSlot.querySelectorAll(
    RECHARTS_RESPONSIVE_CONTAINER_SELECTOR,
  )) {
    if (!(rc instanceof HTMLElement)) {
      continue;
    }
    rc.style.width = `${widthPx}px`;
    rc.style.height = `${heightPx}px`;
    rc.style.maxWidth = `${widthPx}px`;
  }

  for (const wrapper of chartSlot.querySelectorAll(RECHARTS_WRAPPER_SELECTOR)) {
    if (!(wrapper instanceof HTMLElement)) {
      continue;
    }
    wrapper.style.width = `${widthPx}px`;
    wrapper.style.height = `${heightPx}px`;
    wrapper.style.maxWidth = `${widthPx}px`;
    wrapper.style.maxHeight = `${heightPx}px`;
  }

  for (const surface of chartSlot.querySelectorAll(RECHARTS_SURFACE_SELECTOR)) {
    if (!(surface instanceof SVGElement)) {
      continue;
    }
    surface.setAttribute("width", String(widthPx));
    surface.setAttribute("height", String(heightPx));
    surface.style.width = `${widthPx}px`;
    surface.style.height = `${heightPx}px`;
    surface.style.maxWidth = `${widthPx}px`;
    surface.style.display = "block";
  }

  for (const legend of chartSlot.querySelectorAll(
    RECHARTS_LEGEND_WRAPPER_SELECTOR,
  )) {
    if (!(legend instanceof HTMLElement)) {
      continue;
    }
    legend.style.width = `${widthPx}px`;
    legend.style.maxWidth = `${widthPx}px`;
    legend.style.left = "0px";
    legend.style.right = "auto";
  }
};

const pdfChartSlotBaseWidth = (
  chartSlot: HTMLElement,
  liveRectWidth: number,
  widthBasis: number,
): number => {
  const inCard = chartSlot.closest(CARD_CONTENT_SELECTOR) != null;
  const paddingAllowance = inCard ? PDF_CARD_CONTENT_HORIZONTAL_PADDING_PX : 0;
  const maxWidth = Math.max(200, widthBasis - paddingAllowance);
  return Math.min(
    maxWidth,
    Math.max(200, Math.round(liveRectWidth || maxWidth)),
  );
};

/** Removes `id` from `root` and descendants so a briefly attached clone cannot duplicate live document IDs. */
const stripIdsFromElementTree = (root: HTMLElement): void => {
  root.removeAttribute("id");
  for (const el of root.querySelectorAll("[id]")) {
    el.removeAttribute("id");
  }
};

const pdfLayoutWidthBasis = (liveRoot: HTMLElement): number => {
  const liveW = liveRoot.getBoundingClientRect().width;
  return Math.min(
    HERALD_PDF_A4_CONTENT_MAX_WIDTH_PX,
    Math.max(
      320,
      Math.round(liveW || document.documentElement.clientWidth || 800),
    ),
  );
};

/** Constrains the serialized subtree to the printable page width (tables inherit via %). */
const applyPdfLayoutMaxWidthOnClone = (
  clone: HTMLElement,
  widthBasis: number,
): void => {
  clone.style.boxSizing = "border-box";
  clone.style.width = "100%";
  clone.style.setProperty("max-width", `${widthBasis}px`, "important");
};

/**
 * Sizes chart slots from the live DOM and writes explicit px dimensions into the clone.
 * Tailwind for tables/components is injected server-side by naapi; chart slots still need
 * explicit px because Recharts keeps live dimensions in the cloned HTML.
 */
const freezePdfChartSlotSizesOnClone = (
  liveRoot: HTMLElement,
  clone: HTMLElement,
): number => {
  const widthBasis = pdfLayoutWidthBasis(liveRoot);

  stripIdsFromElementTree(clone);

  const liveCharts = liveRoot.querySelectorAll(CHART_SLOT_SELECTOR);
  const cloneCharts = clone.querySelectorAll(CHART_SLOT_SELECTOR);

  cloneCharts.forEach((node, index) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const liveNode = liveCharts[index];
    const liveRect =
      liveNode instanceof HTMLElement
        ? liveNode.getBoundingClientRect()
        : node.getBoundingClientRect();
    const baseW = pdfChartSlotBaseWidth(node, liveRect.width, widthBasis);
    const baseH = Math.max(160, Math.round(liveRect.height || 240));
    const scaledW = Math.max(
      160,
      Math.round(baseW * HERALD_PDF_CHART_WIDTH_SCALE),
    );
    syncPdfRechartsDimensionsOnChartSlot(node, scaledW, baseW, baseH);
  });

  return widthBasis;
};

/**
 * Clones and reshapes the live assistant DOM for PDF export
 *
 * @param root: live `data-herald-response` element.
 * @returns Clone `outerHTML` with chart slots sized, then nodes with {@link HERALD_PDF_EXCLUDE_DATA_ATTR} removed.
 */
export const prepareAssistantHtmlForPdf = (root: HTMLElement): string => {
  const clone = root.cloneNode(true) as HTMLElement;
  const widthBasis = freezePdfChartSlotSizesOnClone(root, clone);
  applyPdfLayoutMaxWidthOnClone(clone, widthBasis);
  clone.querySelectorAll(HERALD_PDF_EXCLUDE_SELECTOR).forEach((el) => {
    el.remove();
  });
  return clone.outerHTML;
};
